import React, { useEffect, useState } from 'react';
import type { CollectionResult, CollectionItem } from '../types';
import { Breadcrumbs } from '../components/Breadcrumbs';
import {
  Search,
  ArrowUpDown,
  Grid,
  List as ListIcon,
  Tag,
  Shield,
  Coins,
  Sparkles,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface CollectionViewPageProps {
  slug: string;
}

export const CollectionViewPage: React.FC<CollectionViewPageProps> = ({ slug }) => {
  const [data, setData] = useState<CollectionResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [activeItem, setActiveItem] = useState<CollectionItem | null>(null);

  // Available tags extracted from current collection
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  useEffect(() => {
    setPage(1);
    setSearchTerm('');
    setSelectedTag('');
    setActiveItem(null);
    setSortKey('name');
  }, [slug]);

  useEffect(() => {
    let isCurrent = true;
    setLoading(true);

    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set('search', searchTerm.trim());
    if (selectedTag) params.set('tag', selectedTag);
    params.set('sort', sortKey);
    params.set('order', sortOrder);
    params.set('page', String(page));
    params.set('limit', '48');

    fetch(`/api/collections/${slug}?${params.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Collection not found (${r.status})`);
        return r.json() as Promise<CollectionResult>;
      })
      .then((res: CollectionResult) => {
        if (!isCurrent) return;
        setData(res);

        // Extract tags for filter bar
        const tagSet = new Set<string>();
        (res.items || []).forEach((item) => {
          (item.tags || []).forEach((t) => tagSet.add(t));
        });
        if (tagSet.size > 0 && availableTags.length === 0) {
          setAvailableTags(Array.from(tagSet).sort());
        }
      })
      .catch((e) => {
        if (!isCurrent) return;
        console.error('Error fetching collection data:', e);
        setData(null);
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [slug, searchTerm, selectedTag, sortKey, sortOrder, page]);

  // Check URL hash for direct item linking
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash && data?.items) {
      const match = data.items.find(
        (it) =>
          String(it.name || it.word || it.title || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-') === decodeURIComponent(hash)
      );
      if (match) setActiveItem(match);
    }
  }, [data]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const breadcrumbs = [
    { label: 'Home', url: '/' },
    { label: data?.title || slug.charAt(0).toUpperCase() + slug.slice(1) },
  ];

  return (
    <div className="collection-view-container">
      {/* Header & Hero */}
      <section className="page-hero py-4">
        <div className="container">
          <Breadcrumbs items={breadcrumbs} />
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h1 className="hero-title display-5 fw-bold mb-1">
                {data?.title || slug.charAt(0).toUpperCase() + slug.slice(1)}
              </h1>
              <p className="text-secondary small mb-0">
                Browsing {data?.total ?? 0} entries in the compendium database.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                >
                  <Grid size={16} />
                </button>
                <button
                  type="button"
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('table')}
                  title="Table View"
                >
                  <ListIcon size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-4">
        {/* Search, Filter & Sort Toolbar */}
        <div className="card shadow-sm border p-3 mb-4 rounded-4">
          <div className="row g-3 align-items-center">
            {/* Search Input */}
            <div className="col-12 col-md-5">
              <div className="input-group">
                <span className="input-group-text border-end-0">
                  <Search size={16} className="text-secondary" />
                </span>
                <input
                  type="search"
                  className="form-control border-start-0"
                  placeholder={`Search ${data?.title || 'entries'}...`}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            {/* Tag Filter */}
            {availableTags.length > 0 && (
              <div className="col-12 col-md-4">
                <div className="input-group">
                  <span className="input-group-text">
                    <Tag size={16} className="text-secondary" />
                  </span>
                  <select
                    className="form-select"
                    value={selectedTag}
                    onChange={(e) => {
                      setSelectedTag(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Tags ({availableTags.length})</option>
                    {availableTags.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Sort Dropdown & Toggle (Sort by ID removed) */}
            <div className="col-12 col-md-3 ms-auto">
              <div className="input-group">
                <select
                  className="form-select"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                >
                  <option value="name">Sort: Name / Word</option>
                  {slug === 'items' && <option value="price">Sort: Price</option>}
                  {slug === 'items' && <option value="defense">Sort: Defense</option>}
                  {slug === 'items' && <option value="bonus">Sort: Bonus</option>}
                  {slug === 'enemies' && <option value="hit_points">Sort: Hit Points</option>}
                  {slug === 'enemies' && <option value="defense">Sort: Defense</option>}
                  {slug === 'enemies' && <option value="credits">Sort: Credits</option>}
                  {slug === 'npcs' && <option value="defense">Sort: Defense</option>}
                  {slug === 'npcs' && <option value="credits">Sort: Credits</option>}
                  {slug === 'abilities' && <option value="cost">Sort: Cost</option>}
                  {slug === 'abilities' && <option value="max">Sort: Max</option>}
                  {slug === 'abilities' && <option value="bonus">Sort: Bonus</option>}
                  {slug === 'skills' && <option value="stat">Sort: Stat</option>}
                  {slug === 'animals' && <option value="hit_points">Sort: Hit Points</option>}
                  {slug === 'animals' && <option value="defense">Sort: Defense</option>}
                  {slug === 'tags' && <option value="type">Sort: Type</option>}
                </select>
                <button
                  type="button"
                  className="btn btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={toggleSortOrder}
                  title={`Toggle sort order (${sortOrder.toUpperCase()})`}
                >
                  <ArrowUpDown size={14} />
                  <span className="small fw-semibold">{sortOrder.toUpperCase()}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Area */}
        {loading ? (
          <div className="py-5 text-center text-secondary">
            <div className="spinner-border text-primary me-2" role="status"></div>
            Loading {data?.title || 'records'}...
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="empty-state p-5 text-center border border-dashed rounded-4 my-4">
            <Info size={32} className="text-secondary mb-2" />
            <h5 className="fw-semibold">No records found</h5>
            <p className="text-secondary small mb-0">Try changing your search terms or clearing the tag filter.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="row g-3">
            {data.items.map((item) => {
              const displayName = item.name || item.word || item.title || `Entry #${item.id}`;
              const anchor = encodeURIComponent(displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-'));

              return (
                <div key={item.id} className="col-12 col-md-6 col-lg-4" id={anchor}>
                  <div
                    className="card h-100 border rounded-4 shadow-sm hover-lift p-3 cursor-pointer d-flex flex-column justify-content-between"
                    onClick={() => setActiveItem(item)}
                  >
                    <div>
                      <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                        <h6 className="card-title fw-bold mb-0 text-primary">{displayName}</h6>
                        {item.price !== undefined && item.price !== null && (
                          <span className="badge bg-warning-subtle d-flex align-items-center gap-1">
                            <Coins size={12} /> {item.price}c
                          </span>
                        )}
                      </div>

                      {/* Stat chips */}
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {item.defense !== undefined && item.defense > 0 && (
                          <span className="badge bg-info-subtle d-flex align-items-center gap-1">
                            <Shield size={12} /> Def {item.defense}
                          </span>
                        )}
                        {item.cost !== undefined && item.cost > 0 && (
                          <span className="badge bg-primary-subtle d-flex align-items-center gap-1">
                            <Sparkles size={12} /> Cost {item.cost}
                          </span>
                        )}
                        {item.hit_points !== undefined && item.hit_points > 0 && (
                          <span className="badge bg-danger-subtle">
                            {item.hit_points} HP
                          </span>
                        )}
                        {item.stat && (
                          <span className="badge bg-secondary-subtle text-uppercase">
                            {item.stat}
                          </span>
                        )}
                      </div>

                      <p className="card-text small line-clamp-3 mb-3">
                        {item.description || item.definition || 'No additional notes provided.'}
                      </p>
                    </div>

                    {/* Tags */}
                    {item.tags && item.tags.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-auto pt-2 border-top">
                        {item.tags.map((t, idx) => (
                          <span key={idx} className="badge bg-secondary-subtle small">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="table-responsive card border shadow-sm rounded-4 overflow-hidden">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Stats / Attributes</th>
                  <th>Description</th>
                  <th>Tags</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id} className="cursor-pointer" onClick={() => setActiveItem(item)}>
                    <td className="fw-semibold text-primary">{item.name || item.word || item.title}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {item.price !== undefined && <span className="badge bg-warning-subtle">{item.price}c</span>}
                        {item.defense !== undefined && item.defense > 0 && <span className="badge bg-info-subtle">Def {item.defense}</span>}
                        {item.cost !== undefined && item.cost > 0 && <span className="badge bg-primary-subtle">Cost {item.cost}</span>}
                        {item.hit_points !== undefined && <span className="badge bg-danger-subtle">{item.hit_points} HP</span>}
                      </div>
                    </td>
                    <td className="small text-secondary max-w-300 text-truncate">{item.description || item.definition || '—'}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(item.tags || []).map((t, ti) => (
                          <span key={ti} className="badge bg-secondary-subtle small">{t}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {data && data.totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between mt-4 flex-wrap gap-2">
            <span className="small text-secondary">
              Page {data.page} of {data.totalPages} ({data.total} total items)
            </span>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                disabled={page >= data.totalPages}
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Item Detail Inspector Modal */}
      {activeItem && (
        <div className="modal-backdrop-custom" onClick={() => setActiveItem(null)}>
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content shadow-lg border-0 rounded-4">
              <div className="modal-header">
                <h5 className="modal-title fw-bold text-primary">
                  {activeItem.name || activeItem.word || activeItem.title}
                </h5>
                <button type="button" className="btn-close" onClick={() => setActiveItem(null)}></button>
              </div>

              <div className="modal-body p-4">
                {/* Stats Grid */}
                <div className="row g-2 mb-3">
                  {Object.entries(activeItem)
                    .filter(([key, val]) => !['id', 'name', 'word', 'title', 'description', 'definition', 'tags', 'raw_tags'].includes(key) && val !== null && val !== '')
                    .map(([key, val]) => (
                      <div key={key} className="col-6 col-md-4">
                        <div className="p-2 border rounded card">
                          <small className="text-secondary text-uppercase fw-semibold d-block">
                            {key.replace(/_/g, ' ')}
                          </small>
                          <span className="fw-bold">{String(val)}</span>
                        </div>
                      </div>
                    ))}
                </div>

                {/* Description */}
                {(activeItem.description || activeItem.definition) && (
                  <div className="mb-3">
                    <h6 className="fw-bold text-secondary mb-1">Description / Details</h6>
                    <div className="p-3 card rounded-3 small">
                      {activeItem.description || activeItem.definition}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {activeItem.tags && activeItem.tags.length > 0 && (
                  <div>
                    <h6 className="fw-bold text-secondary mb-1">Tags</h6>
                    <div className="d-flex flex-wrap gap-1">
                      {activeItem.tags.map((t, idx) => (
                        <span key={idx} className="badge bg-primary-subtle">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setActiveItem(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
